import posthog from 'posthog-js';
import type { GatsbyBrowser } from 'gatsby';
import {
  checkRepeatedPageviews,
  isBotSession,
  storage,
  THRESHOLDS,
} from './src/helpers/anomaly';

const LOCALIZED_LANGS = ['es', 'pt'];
const DEFAULT_LANG = 'en';

// exclude page and children
const EXCLUDED_PATHS = [
  '/stablecoin-calculator',
  '/faq',
  '/document',
  '/policies',
  '/careers',
  '/create-your-role',
];
// exclude only children
const EXCLUDED_CHILDREN = ['/user-guide'];

const getTargetLang = () =>
  (navigator.languages || [navigator.language])
    .map((lang) => lang.split('-')[0])
    .find((prefix) => [...LOCALIZED_LANGS, DEFAULT_LANG].includes(prefix)) ||
  DEFAULT_LANG;

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  posthog.init(process.env.GATSBY_POSTHOG_API_KEY!, {
    api_host: process.env.GATSBY_POSTHOG_API_HOST!,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_performance: false,
    cross_subdomain_cookie: true,
    // session_recording: {
    //   sampleRate: 0.1,
    // },
    before_send: (event) => {
      if (event?.event === '$web_vitals') return null;

      if (isBotSession()) {
        if (
          event?.event?.startsWith('$') ||
          event?.event === 'anomaly_detected'
        ) {
          return event;
        }

        const count = Number(storage.get('ph_bot_events') ?? '0') + 1;
        storage.set('ph_bot_events', String(count));

        if (count > THRESHOLDS.EVENTS_AFTER_ANOMALY) return null;
      }

      return event;
    },
  });

  // language preselect
  const { pathname } = window.location;
  const alreadyLocalized = LOCALIZED_LANGS.some(
    (lang) => pathname === `/${lang}` || pathname.startsWith(`/${lang}/`),
  );
  if (alreadyLocalized) return;

  const excluded =
    EXCLUDED_PATHS.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    ) || EXCLUDED_CHILDREN.some((p) => pathname.startsWith(`${p}/`));
  if (excluded) return;

  const lang = localStorage.getItem('lang-preference') || getTargetLang();
  if (lang && lang !== DEFAULT_LANG)
    window.location.replace(`/${lang}${pathname}`);
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
  checkRepeatedPageviews(location.pathname);
};
